import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, MessageSquare, Edit3, Check, X, Users, Clock, Loader2,
  ThumbsUp, ThumbsDown, Scale, Send, History, RefreshCw, Trash2, CheckCircle2,
  AlertTriangle, Zap, Minus, UserPlus, Search
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { getNeed, addNeedComment, updateNeedJd, confirmNeed, deleteNeed, getNeedDebateStatus, redebateNeed, updateNeedMembers, getMentionableUsers } from '../api';
import { HiringNeedDetail, NeedDebate, User, URGENCY_LABELS, NEED_STATUS_LABELS, NeedUrgency, NeedStatus } from '../types';

interface Props {
  needId: string;
  currentUser: User;
  onBack: () => void;
  onUpdate: () => void;
  notificationBell?: React.ReactNode;
}

const urgencyColors: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  normal: 'bg-blue-50 text-blue-600',
  high: 'bg-orange-50 text-orange-600',
  urgent: 'bg-red-50 text-red-600',
};

const statusColors: Record<string, string> = {
  debating: 'bg-purple-50 text-purple-600 border-purple-200',
  discussing: 'bg-blue-50 text-blue-600 border-blue-200',
  confirmed: 'bg-green-50 text-green-600 border-green-200',
  cancelled: 'bg-gray-50 text-gray-500 border-gray-200',
};

export default function NeedDetail({ needId, currentUser, onBack, onUpdate, notificationBell }: Props) {
  const [need, setNeed] = useState<HiringNeedDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [debateReady, setDebateReady] = useState(false);
  const [pollingDebate, setPollingDebate] = useState(false);
  // Comment
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const commentEndRef = useRef<HTMLDivElement>(null);
  // @mention
  const [showMention, setShowMention] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStartPos, setMentionStartPos] = useState(-1);
  const [mentionedIds, setMentionedIds] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  // JD editing
  const [editingJd, setEditingJd] = useState(false);
  const [jdTitle, setJdTitle] = useState('');
  const [jdResp, setJdResp] = useState('');
  const [jdReq, setJdReq] = useState('');
  const [needSource, setNeedSource] = useState('');
  const [firstInterviewerIds, setFirstInterviewerIds] = useState<string[]>([]);
  const [secondInterviewerIds, setSecondInterviewerIds] = useState<string[]>([]);
  const [savingJd, setSavingJd] = useState(false);
  const [interviewerUsers, setInterviewerUsers] = useState<User[]>([]);
  // Tab
  const [activeTab, setActiveTab] = useState<'debate' | 'discuss' | 'jd' | 'history'>('debate');
  // Confirm
  const [confirming, setConfirming] = useState(false);
  // Member management
  const [showMemberPanel, setShowMemberPanel] = useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [memberSearchQ, setMemberSearchQ] = useState('');
  const [pendingMemberIds, setPendingMemberIds] = useState<string[]>([]);
  const [savingMembers, setSavingMembers] = useState(false);

  const isCreator = need?.created_by === currentUser.id;
  const isConfirmed = need?.status === 'confirmed';
  // 群聊模式：需求的任意成员均可协作（确认终版、管理成员），仅创建人可删除
  const isMember = isCreator || (need?.members || []).some(m => m.user_id === currentUser.id);
  const canManageNeed = isMember;

  const fetchDetail = useCallback(async () => {
    try {
      const data = await getNeed(needId);
      setNeed(data);
      setJdTitle(data.jd_title || '');
      setJdResp(data.jd_responsibilities || '');
      setJdReq(data.jd_requirements || '');
      const parseIds = (v: any) => { try { return Array.isArray(v) ? v : JSON.parse(v || '[]'); } catch { return []; } };
      setNeedSource(data.need_source || '');
      setFirstInterviewerIds(parseIds(data.first_interviewer_ids));
      setSecondInterviewerIds(parseIds(data.second_interviewer_ids));
      // Check debate status
      if (data.debates && data.debates.length >= 3) {
        setDebateReady(true);
      }
    } catch (err) {
      toast.error('加载需求详情失败');
    } finally {
      setLoading(false);
    }
  }, [needId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  useEffect(() => {
    getMentionableUsers().then((users: User[]) => {
      setInterviewerUsers(users.filter(u => u.role !== 'referrer'));
    }).catch(() => {});
  }, []);

  // Poll for debate completion if status is 'debating'
  useEffect(() => {
    if (!need || need.status !== 'debating') return;
    setPollingDebate(true);
    const interval = setInterval(async () => {
      try {
        const status = await getNeedDebateStatus(needId);
        if (status.ready) {
          clearInterval(interval);
          setPollingDebate(false);
          fetchDetail();
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [need?.status, needId, fetchDetail]);

  // Get mention candidates from need members
  const mentionCandidates = (need?.members || []).filter(m => {
    if (m.user_id === currentUser.id) return false;
    if (!mentionQuery) return true;
    const q = mentionQuery.toLowerCase();
    return (m.display_name || m.username || '').toLowerCase().includes(q);
  });

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const pos = e.target.selectionStart || 0;
    setCommentText(val);

    // Detect @ trigger
    const textBeforeCursor = val.substring(0, pos);
    const atMatch = textBeforeCursor.match(/@([^@\s]*)$/);
    if (atMatch) {
      setShowMention(true);
      setMentionQuery(atMatch[1]);
      setMentionStartPos(pos - atMatch[0].length);
      setMentionIndex(0);
    } else {
      setShowMention(false);
      setMentionQuery('');
      setMentionStartPos(-1);
    }
  };

  const insertMention = (member: typeof mentionCandidates[0]) => {
    const name = member.display_name || member.username;
    const before = commentText.substring(0, mentionStartPos);
    const after = commentText.substring(mentionStartPos + mentionQuery.length + 1); // +1 for @
    const newText = `${before}@${name} ${after}`;
    setCommentText(newText);
    setShowMention(false);
    setMentionQuery('');
    setMentionStartPos(-1);
    if (!mentionedIds.includes(member.user_id)) {
      setMentionedIds(prev => [...prev, member.user_id]);
    }
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleCommentKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showMention && mentionCandidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(i => Math.min(i + 1, mentionCandidates.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionCandidates[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMention(false);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !showMention) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || sendingComment) return;
    setSendingComment(true);
    try {
      // Extract mentioned user IDs from the text
      const finalMentionedIds = mentionedIds.filter(uid => {
        const member = need?.members.find(m => m.user_id === uid);
        if (!member) return false;
        const name = member.display_name || member.username;
        return commentText.includes(`@${name}`);
      });
      await addNeedComment(needId, { content: commentText.trim(), mentioned_user_ids: finalMentionedIds });
      setCommentText('');
      setMentionedIds([]);
      await fetchDetail();
      setTimeout(() => commentEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {
      toast.error('发送评论失败');
    } finally {
      setSendingComment(false);
    }
  };

  // Render @mentions as highlighted spans
  const renderMentionText = (text: string) => {
    const parts = text.split(/(@\S+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@') && part.length > 1) {
        const name = part.substring(1);
        const isMember = need?.members.some(m => (m.display_name || m.username) === name);
        if (isMember) {
          return <span key={i} className="font-medium text-primary-600 bg-primary-50 rounded px-0.5">{part}</span>;
        }
      }
      return <span key={i}>{part}</span>;
    });
  };

  const handleSaveJd = async () => {
    setSavingJd(true);
    try {
      await updateNeedJd(needId, {
        jd_title: jdTitle,
        jd_responsibilities: jdResp,
        jd_requirements: jdReq,
        need_source: needSource,
        first_interviewer_ids: firstInterviewerIds,
        second_interviewer_ids: secondInterviewerIds,
      });
      toast.success('JD已保存');
      setEditingJd(false);
      await fetchDetail();
    } catch {
      toast.error('保存JD失败');
    } finally {
      setSavingJd(false);
    }
  };

  const handleConfirm = async () => {
    if (!window.confirm('确认终版岗位？将自动在招聘系统创建正式岗位。')) return;
    setConfirming(true);
    try {
      await confirmNeed(needId);
      toast.success('岗位已创建，进入招聘流程！');
      await fetchDetail();
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '确认失败');
    } finally {
      setConfirming(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('确认删除该需求？此操作不可恢复。')) return;
    try {
      await deleteNeed(needId);
      toast.success('需求已删除');
      onUpdate();
      onBack();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '删除失败');
    }
  };

  const openMemberPanel = async () => {
    try {
      const users = await getMentionableUsers();
      setAllUsers(users.filter((u: User) => u.role !== 'referrer'));
    } catch {}
    // Pre-select current non-creator members
    const current = (need?.members || []).filter(m => m.role !== 'creator').map(m => m.user_id);
    setPendingMemberIds(current);
    setMemberSearchQ('');
    setShowMemberPanel(true);
  };

  const togglePendingMember = (uid: string) => {
    setPendingMemberIds(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const handleSaveMembers = async () => {
    setSavingMembers(true);
    try {
      await updateNeedMembers(needId, pendingMemberIds);
      toast.success('协作成员已更新');
      setShowMemberPanel(false);
      await fetchDetail();
    } catch {
      toast.error('更新成员失败');
    } finally {
      setSavingMembers(false);
    }
  };

  const handleRedebate = async () => {
    if (!window.confirm('重新触发AI辩论？原辩论记录将被覆盖。')) return;
    try {
      await redebateNeed(needId);
      toast.success('AI重新辩论中...');
      await fetchDetail();
    } catch {
      toast.error('重新辩论失败');
    }
  };

  if (loading || !need) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  const proDebate = need.debates.find(d => d.side === 'pro');
  const conDebate = need.debates.find(d => d.side === 'con');
  const judgeDebate = need.debates.find(d => d.side === 'judge');

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 md:px-6 py-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold text-gray-900 truncate">
                {need.jd_title || need.department || '新需求'}
              </h1>
              <span className={clsx('px-2 py-0.5 rounded-full text-[11px] font-medium border', statusColors[need.status])}>
                {NEED_STATUS_LABELS[need.status as NeedStatus]}
              </span>
              <span className={clsx('px-2 py-0.5 rounded-full text-[11px] font-medium', urgencyColors[need.urgency])}>
                {URGENCY_LABELS[need.urgency as NeedUrgency]}
              </span>
            </div>
            <p className="text-[11px] text-gray-400 mt-0.5">
              {need.creator_name} 创建于 {need.created_at} · {need.member_count || need.members.length} 人协作
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {canManageNeed && (
              <button onClick={openMemberPanel} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors" title="管理协作成员">
                <UserPlus size={16} />
              </button>
            )}
            {notificationBell}
            {!isConfirmed && (
              <button onClick={handleRedebate} className="p-1.5 rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors" title="重新AI辩论">
                <RefreshCw size={16} />
              </button>
            )}
            {isCreator && !isConfirmed && (
              <button onClick={handleDelete} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="删除需求">
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 md:px-6 flex gap-0 overflow-x-auto no-scrollbar">
        {([
          { key: 'debate', label: 'AI辩论', icon: Scale },
          { key: 'jd', label: 'JD编辑', icon: Edit3 },
          { key: 'discuss', label: '讨论区', icon: MessageSquare, count: need.comments.length },
          { key: 'history', label: '修改记录', icon: History, count: need.jd_history.length },
        ] as { key: typeof activeTab; label: string; icon: any; count?: number }[]).map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={clsx(
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
              activeTab === t.key ? 'text-primary-600 border-primary-600' : 'text-gray-500 border-transparent hover:text-gray-700'
            )}
          >
            <t.icon size={14} />
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-gray-100 text-gray-500">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto">
        {/* ===== Need Info Card ===== */}
        <div className="mx-4 md:mx-6 mt-4 bg-white rounded-xl border border-gray-200 p-4 md:p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-400 text-xs">部门</span>
              <p className="text-gray-800 font-medium mt-0.5">{need.department || '未指定'}</p>
            </div>
            <div>
              <span className="text-gray-400 text-xs">需求人数</span>
              <p className="text-gray-800 font-medium mt-0.5">{need.headcount} 人</p>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-400 text-xs">业务问题 / 需求背景</span>
              <p className="text-gray-800 mt-0.5 whitespace-pre-wrap">{need.business_problem}</p>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-400 text-xs">协作成员</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {need.members.map(m => (
                  <span key={m.user_id} className={clsx(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium',
                    m.role === 'creator' ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-600'
                  )}>
                    {m.display_name || m.username}
                    {m.role === 'creator' && <span className="text-[9px]">(创建人)</span>}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ===== AI Debate Tab ===== */}
        {activeTab === 'debate' && (
          <div className="mx-4 md:mx-6 mt-4 mb-6 space-y-4">
            {pollingDebate || need.status === 'debating' ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Loader2 size={32} className="animate-spin text-purple-400 mb-3" />
                <p className="text-sm font-medium text-gray-600">AI 正在进行三方辩论...</p>
                <p className="text-xs text-gray-400 mt-1">正方、反方、裁判正在激烈讨论中</p>
              </div>
            ) : (
              <>
                {/* Pro */}
                {proDebate && (
                  <div className="bg-white rounded-xl border border-green-200 overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-green-50 border-b border-green-100">
                      <ThumbsUp size={15} className="text-green-600" />
                      <span className="text-sm font-bold text-green-700">正方 · 支持设立</span>
                    </div>
                    <div className="px-4 py-3 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {proDebate.content}
                    </div>
                  </div>
                )}

                {/* Con */}
                {conDebate && (
                  <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border-b border-red-100">
                      <ThumbsDown size={15} className="text-red-600" />
                      <span className="text-sm font-bold text-red-700">反方 · 质疑合理性</span>
                    </div>
                    <div className="px-4 py-3 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {conDebate.content}
                    </div>
                  </div>
                )}

                {/* Judge */}
                {judgeDebate && (
                  <div className="bg-white rounded-xl border border-purple-200 overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-purple-50 border-b border-purple-100">
                      <Scale size={15} className="text-purple-600" />
                      <span className="text-sm font-bold text-purple-700">裁判 · 最终结论</span>
                    </div>
                    <div className="px-4 py-3 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {judgeDebate.content}
                    </div>
                  </div>
                )}

                {!proDebate && !conDebate && !judgeDebate && (
                  <div className="text-center py-12 text-gray-400">
                    <Scale size={40} strokeWidth={1} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-sm">暂无AI辩论记录</p>
                    <button onClick={handleRedebate} className="mt-3 px-4 py-2 text-sm text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                      触发AI辩论
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ===== JD Edit Tab ===== */}
        {activeTab === 'jd' && (
          <div className="mx-4 md:mx-6 mt-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-gray-800">岗位JD</h3>
                {!editingJd && (
                  <button onClick={() => setEditingJd(true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors">
                    <Edit3 size={12} />编辑
                  </button>
                )}
              </div>

              {editingJd ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">岗位名称</label>
                    <input value={jdTitle} onChange={e => setJdTitle(e.target.value)}
                      className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                      placeholder="如：高级Java工程师" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">岗位职责</label>
                    <textarea value={jdResp} onChange={e => setJdResp(e.target.value)} rows={8}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                      placeholder="请输入岗位职责..." />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">任职要求</label>
                    <textarea value={jdReq} onChange={e => setJdReq(e.target.value)} rows={8}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                      placeholder="请输入任职要求..." />
                  </div>
                  {/* Need source */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">需求来源</label>
                    <select value={needSource} onChange={e => setNeedSource(e.target.value)}
                      className="w-full h-8 px-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-300 bg-white">
                      <option value="">未指定</option>
                      <option value="业务增长">业务增长</option>
                      <option value="人员替换">人员替换</option>
                      <option value="新业务拓展">新业务拓展</option>
                      <option value="内部调动">内部调动</option>
                      <option value="为实习生">为实习生</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                  {/* First interviewer selector */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1.5 block">一面面试官</label>
                    <div className="flex flex-wrap gap-1.5 mb-1.5">
                      {firstInterviewerIds.map(uid => {
                        const u = interviewerUsers.find(x => x.id === uid);
                        return u ? (
                          <span key={uid} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">
                            {u.display_name || u.username}
                            <button onClick={() => setFirstInterviewerIds(prev => prev.filter(id => id !== uid))} className="hover:text-red-500"><X size={10} /></button>
                          </span>
                        ) : null;
                      })}
                    </div>
                    <select
                      value=""
                      onChange={e => { const v = e.target.value; if (v && !firstInterviewerIds.includes(v)) setFirstInterviewerIds(prev => [...prev, v]); }}
                      className="w-full h-8 px-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-300">
                      <option value="">+ 添加一面面试官</option>
                      {interviewerUsers.filter(u => !firstInterviewerIds.includes(u.id)).map(u => (
                        <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                      ))}
                    </select>
                  </div>
                  {/* Second interviewer selector */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1.5 block">二面面试官</label>
                    <div className="flex flex-wrap gap-1.5 mb-1.5">
                      {secondInterviewerIds.map(uid => {
                        const u = interviewerUsers.find(x => x.id === uid);
                        return u ? (
                          <span key={uid} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs">
                            {u.display_name || u.username}
                            <button onClick={() => setSecondInterviewerIds(prev => prev.filter(id => id !== uid))} className="hover:text-red-500"><X size={10} /></button>
                          </span>
                        ) : null;
                      })}
                    </div>
                    <select
                      value=""
                      onChange={e => { const v = e.target.value; if (v && !secondInterviewerIds.includes(v)) setSecondInterviewerIds(prev => [...prev, v]); }}
                      className="w-full h-8 px-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-300">
                      <option value="">+ 添加二面面试官</option>
                      {interviewerUsers.filter(u => !secondInterviewerIds.includes(u.id)).map(u => (
                        <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <button onClick={handleSaveJd} disabled={savingJd}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50">
                      {savingJd ? '保存中...' : '保存'}
                    </button>
                    <button onClick={() => { const p = (v: any) => { try { return Array.isArray(v) ? v : JSON.parse(v || '[]'); } catch { return []; } }; setEditingJd(false); setJdTitle(need.jd_title); setJdResp(need.jd_responsibilities); setJdReq(need.jd_requirements); setNeedSource(need.need_source || ''); setFirstInterviewerIds(p(need.first_interviewer_ids)); setSecondInterviewerIds(p(need.second_interviewer_ids)); }}
                      className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">岗位名称</p>
                    <p className="text-sm text-gray-800 font-medium">{need.jd_title || <span className="text-gray-300 italic">未填写</span>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">岗位职责</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {need.jd_responsibilities || <span className="text-gray-300 italic">未填写</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">任职要求</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {need.jd_requirements || <span className="text-gray-300 italic">未填写</span>}
                    </p>
                  </div>
                  {need.need_source && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1">需求来源</p>
                      <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{need.need_source}</span>
                    </div>
                  )}
                  {(() => {
                    const parseIds = (v: any) => { try { return Array.isArray(v) ? v : JSON.parse(v || '[]'); } catch { return []; } };
                    const firstIds: string[] = parseIds(need.first_interviewer_ids);
                    const secondIds: string[] = parseIds(need.second_interviewer_ids);
                    if (!firstIds.length && !secondIds.length) return null;
                    return (
                      <div className="space-y-2 pt-1 border-t border-gray-100">
                        {firstIds.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-400 mb-1">一面面试官</p>
                            <div className="flex flex-wrap gap-1.5">
                              {firstIds.map(uid => {
                                const u = interviewerUsers.find(x => x.id === uid);
                                return u ? <span key={uid} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">{u.display_name || u.username}</span> : null;
                              })}
                            </div>
                          </div>
                        )}
                        {secondIds.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-400 mb-1">二面面试官</p>
                            <div className="flex flex-wrap gap-1.5">
                              {secondIds.map(uid => {
                                const u = interviewerUsers.find(x => x.id === uid);
                                return u ? <span key={uid} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs">{u.display_name || u.username}</span> : null;
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Confirm button */}
            {canManageNeed && !isConfirmed && (
              <div className="mt-4">
                <button onClick={handleConfirm} disabled={confirming || !need.jd_title?.trim()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-lg shadow-green-200 transition-all disabled:opacity-50 disabled:shadow-none">
                  <CheckCircle2 size={18} />
                  {confirming ? '正在创建岗位...' : '确认终版岗位 → 自动创建招聘岗位'}
                </button>
                <p className="text-[11px] text-gray-400 text-center mt-2">确认后将自动在招聘系统生成正式岗位，并进入招聘流程</p>
              </div>
            )}

            {isConfirmed && need.position_id && (
              <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-green-600" />
                  <span className="text-sm font-medium text-green-700">已创建正式岗位</span>
                </div>
                <p className="text-xs text-green-600 mt-1">
                  确认人：{need.confirmed_by || '-'} · {need.confirmed_at}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ===== Discussion Tab ===== */}
        {activeTab === 'discuss' && (
          <div className="mx-4 md:mx-6 mt-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200">
              {/* Comments list */}
              <div className="max-h-[500px] overflow-y-auto p-4 space-y-3">
                {need.comments.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <MessageSquare size={32} strokeWidth={1} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-sm">暂无讨论，发表第一条评论吧</p>
                  </div>
                )}
                {need.comments.map(c => {
                  const isMine = c.user_id === currentUser.id;
                  return (
                    <div key={c.id} className={clsx('flex gap-2.5', isMine && 'flex-row-reverse')}>
                      <div className="shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-600">
                        {(c.author_name || '?').charAt(0)}
                      </div>
                      <div className={clsx('max-w-[75%]', isMine ? 'text-right' : '')}>
                        <div className="flex items-center gap-2 mb-0.5" style={{ justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
                          <span className="text-xs font-medium text-gray-700">{c.author_name}</span>
                          <span className="text-[10px] text-gray-400">{c.created_at}</span>
                        </div>
                        <div className={clsx(
                          'inline-block px-3 py-2 rounded-xl text-sm',
                          isMine ? 'bg-primary-500 text-white rounded-tr-sm' : 'bg-gray-100 text-gray-700 rounded-tl-sm'
                        )}>
                          <p className="whitespace-pre-wrap text-left">{renderMentionText(c.content)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={commentEndRef} />
              </div>

              {/* Comment input */}
              <div className="border-t border-gray-100 p-3">
                  <div className="relative">
                    {/* @mention dropdown */}
                    {showMention && mentionCandidates.length > 0 && (
                      <div className="absolute bottom-full left-0 mb-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-10 py-1 max-h-48 overflow-y-auto">
                        <div className="px-3 py-1.5 text-[10px] font-medium text-gray-400 uppercase">选择成员</div>
                        {mentionCandidates.map((m, idx) => (
                          <button key={m.user_id}
                            onMouseDown={e => { e.preventDefault(); insertMention(m); }}
                            className={clsx(
                              'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors',
                              idx === mentionIndex ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50 text-gray-700'
                            )}
                          >
                            <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-[10px] font-bold text-primary-600 shrink-0">
                              {(m.display_name || m.username || '?').charAt(0)}
                            </div>
                            <div className="min-w-0">
                              <span className="text-xs font-medium">{m.display_name || m.username}</span>
                              {m.role === 'creator' && <span className="ml-1 text-[10px] text-gray-400">(创建人)</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        ref={inputRef}
                        value={commentText}
                        onChange={handleCommentChange}
                        onKeyDown={handleCommentKeyDown}
                        onBlur={() => setTimeout(() => setShowMention(false), 200)}
                        placeholder="输入评论，@ 可提及成员..."
                        className="flex-1 h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                      />
                      <button onClick={handleAddComment} disabled={!commentText.trim() || sendingComment}
                        className="shrink-0 p-2 rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 transition-colors">
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        )}

        {/* ===== History Tab ===== */}
        {activeTab === 'history' && (
          <div className="mx-4 md:mx-6 mt-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              {need.jd_history.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <History size={32} strokeWidth={1} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-sm">暂无修改记录</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {need.jd_history.map(h => {
                    const fieldLabel = h.field === 'jd_title' ? '岗位名称' : h.field === 'jd_responsibilities' ? '岗位职责' : h.field === 'jd_requirements' ? '任职要求' : h.field;
                    return (
                      <div key={h.id} className="border border-gray-100 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium text-gray-700">{h.editor_name}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600">{fieldLabel}</span>
                          <span className="text-[10px] text-gray-400 ml-auto">{h.created_at}</span>
                        </div>
                        {h.old_value && (
                          <div className="mb-1.5 p-2 bg-red-50/50 rounded text-xs text-red-700 line-through max-h-20 overflow-hidden">
                            {h.old_value.substring(0, 200)}{h.old_value.length > 200 ? '...' : ''}
                          </div>
                        )}
                        <div className="p-2 bg-green-50/50 rounded text-xs text-green-700 max-h-20 overflow-hidden">
                          {h.new_value.substring(0, 200)}{h.new_value.length > 200 ? '...' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Member management panel */}
      {showMemberPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMemberPanel(false)} />
          <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-bold text-gray-900">管理协作成员</h2>
              <button onClick={() => setShowMemberPanel(false)} className="p-1 rounded-full hover:bg-gray-100 text-gray-400">
                <X size={16} />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 pt-3 pb-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={memberSearchQ} onChange={e => setMemberSearchQ(e.target.value)}
                  placeholder="搜索成员..."
                  className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:bg-white" />
              </div>
            </div>

            {/* Current creator (locked) */}
            <div className="px-4 pb-1">
              <p className="text-[10px] text-gray-400 font-medium uppercase mb-1">创建人（不可移除）</p>
              {need.members.filter(m => m.role === 'creator').map(m => (
                <div key={m.user_id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-primary-50/50">
                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-[11px] font-bold text-primary-600">
                    {(m.display_name || m.username || '?').charAt(0)}
                  </div>
                  <span className="text-xs font-medium text-primary-700">{m.display_name || m.username}</span>
                  <span className="text-[10px] text-primary-400 ml-auto">创建人</span>
                </div>
              ))}
            </div>

            {/* User list */}
            <div className="flex-1 overflow-y-auto px-4 py-2">
              <p className="text-[10px] text-gray-400 font-medium uppercase mb-1">选择协作成员</p>
              <div className="space-y-0.5">
                {allUsers
                  .filter(u => u.id !== need.created_by && u.status === 'active')
                  .filter(u => !memberSearchQ || (u.display_name || u.username).toLowerCase().includes(memberSearchQ.toLowerCase()))
                  .map(u => {
                    const selected = pendingMemberIds.includes(u.id);
                    return (
                      <button key={u.id} onClick={() => togglePendingMember(u.id)}
                        className={clsx(
                          'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors',
                          selected ? 'bg-primary-50' : 'hover:bg-gray-50'
                        )}
                      >
                        <div className={clsx(
                          'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0',
                          selected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                        )}>
                          {selected && <Check size={12} className="text-white" strokeWidth={3} />}
                        </div>
                        <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500 shrink-0">
                          {(u.display_name || u.username).charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-medium text-gray-700">{u.display_name || u.username}</span>
                          <span className="ml-2 text-[10px] text-gray-400">
                            {u.role === 'admin' ? '管理员' : u.role === 'hr' ? 'HR' : '面试官'}
                          </span>
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* Footer */}
            <div className="shrink-0 px-5 py-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-[11px] text-gray-400">已选 {pendingMemberIds.length} 人</span>
              <div className="flex gap-2">
                <button onClick={() => setShowMemberPanel(false)} className="px-4 py-2 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  取消
                </button>
                <button onClick={handleSaveMembers} disabled={savingMembers}
                  className="px-5 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50">
                  {savingMembers ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
